import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  BarChart3,
  PieChart,
  Users,
  DollarSign,
  Calendar,
  Download,
  Loader,
  AlertCircle
} from 'lucide-react';
import { analyticsAPI, userAPI, transactionAPI } from '../utils/api';

const Analytics = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for analytics data
  const [stats, setStats] = useState({
    totalVolume: 0,
    totalTransactions: 0,
    averageTransaction: 0,
    successRate: 0,
    activeUsers: 0,
    totalUsers: 0
  });

  const [volumeData, setVolumeData] = useState([]);
  const [topCryptos, setTopCryptos] = useState([]);
  const [userGrowth, setUserGrowth] = useState({
    newUsers: 0,
    retentionRate: 0
  });

  const periods = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 3 months' },
    { value: '1y', label: 'Last year' }
  ];

  // Fetch analytics data on component mount and period change
  useEffect(() => {
    fetchAnalyticsData();
  }, [selectedPeriod]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch data in parallel
      const [
        userStatsRes,
        transactionStatsRes,
        volumeStatsRes,
        userGrowthRes
      ] = await Promise.all([
        userAPI.getUserStats(),
        transactionAPI.getTransactionStats(),
        analyticsAPI.getVolumeStats({ period: selectedPeriod }),
        analyticsAPI.getUserGrowthStats()
      ]);

      // Process user stats
      if (userStatsRes.data.success) {
        const userData = userStatsRes.data.data;
        setStats(prev => ({
          ...prev,
          totalUsers: userData.users?.total || 0,
          activeUsers: userData.users?.active || 0
        }));
      }

      // Process transaction stats
      if (transactionStatsRes.data.success) {
        const txData = transactionStatsRes.data.data;
        setStats(prev => ({
          ...prev,
          totalTransactions: txData.counts?.total || 0,
          totalVolume: txData.volume?.total || 0,
          successRate: parseFloat(txData.counts?.success_rate || 0),
          averageTransaction: txData.volume?.average || 0
        }));
      }

      // Process volume data (daily breakdown)
      if (volumeStatsRes.data.success) {
        const volumeBreakdown = volumeStatsRes.data.data.daily_breakdown || [];

        // Format volume data for charts
        const formattedVolumeData = volumeBreakdown.map(item => ({
          date: formatChartDate(item.date),
          amount: item.total_amount || 0,
          onramp: item.onramp_amount || 0,
          offramp: item.offramp_amount || 0,
          total: item.total_count || 0
        }));

        setVolumeData(formattedVolumeData);

        // Extract top cryptocurrencies from volume data
        const cryptoMap = {};
        volumeBreakdown.forEach(day => {
          if (day.by_currency) {
            day.by_currency.forEach(crypto => {
              if (!cryptoMap[crypto.currency]) {
                cryptoMap[crypto.currency] = {
                  name: crypto.currency,
                  volume: 0,
                  transactions: 0
                };
              }
              cryptoMap[crypto.currency].volume += crypto.amount || 0;
              cryptoMap[crypto.currency].transactions += crypto.count || 0;
            });
          }
        });

        // Convert to array and sort by volume
        const cryptoArray = Object.values(cryptoMap)
          .sort((a, b) => b.volume - a.volume)
          .slice(0, 5);

        // Calculate percentages
        const totalCryptoVolume = cryptoArray.reduce((sum, c) => sum + c.volume, 0);
        const formattedCryptos = cryptoArray.map(crypto => ({
          ...crypto,
          percentage: totalCryptoVolume > 0
            ? ((crypto.volume / totalCryptoVolume) * 100).toFixed(1)
            : 0
        }));

        setTopCryptos(formattedCryptos);
      }

      // Process user growth data
      if (userGrowthRes.data.success) {
        const growthData = userGrowthRes.data.data;
        setUserGrowth({
          newUsers: growthData.new_users_this_week || 0,
          retentionRate: growthData.retention_rate || 0
        });
      }

    } catch (error) {
      console.error('Error fetching analytics data:', error);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatChartDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';

    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    return `${month} ${day}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const SimpleChart = ({ data, type = 'line' }) => {
    if (!data || data.length === 0) {
      return (
        <div className="flex items-center justify-center h-32 text-vetme-gray-dark">
          No data available
        </div>
      );
    }

    const maxValue = Math.max(...data.map(d => d.total || d.amount || 0));

    return (
      <div className="flex items-end space-x-2 h-32">
        {data.map((item, index) => {
          const height = maxValue > 0 ? (((item.total || item.amount) / maxValue) * 100) : 0;
          return (
            <div key={index} className="flex-1 flex flex-col items-center">
              <div
                className="w-full bg-vetme-mint rounded-t transition-all duration-300 hover:bg-vetme-mint-dark"
                style={{ height: `${height}%`, minHeight: height > 0 ? '4px' : '0' }}
              />
              <div className="text-xs text-vetme-gray-dark mt-2 text-center">
                {item.date}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Calculate performance metrics with trends (comparing to previous period)
  const performanceMetrics = [
    {
      name: 'Total Volume',
      current: stats.totalVolume,
      change: 16.05, // This would come from backend comparison
      trend: 'up'
    },
    {
      name: 'Transaction Count',
      current: stats.totalTransactions,
      change: 17.45, // This would come from backend comparison
      trend: 'up'
    },
    {
      name: 'Average Transaction',
      current: stats.averageTransaction,
      change: -1.19, // This would come from backend comparison
      trend: 'down'
    },
    {
      name: 'Success Rate',
      current: stats.successRate,
      change: 0.41, // This would come from backend comparison
      trend: 'up'
    }
  ];

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader className="w-12 h-12 text-vetme-mint-dark animate-spin mx-auto" />
          <p className="mt-4 text-vetme-gray-dark">Loading analytics...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start">
            <AlertCircle className="w-6 h-6 text-red-600 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-red-800 font-medium">Error Loading Analytics</h3>
              <p className="text-red-600 mt-1">{error}</p>
              <button
                onClick={fetchAnalyticsData}
                className="mt-4 btn-vetme-primary"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="vetme-heading-big text-vetme-dark">Analytics</h1>
          <p className="mt-2 text-vetme-gray-dark">
            Comprehensive insights and performance metrics for VetmePay.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="input-vetme"
          >
            {periods.map(period => (
              <option key={period.value} value={period.value}>
                {period.label}
              </option>
            ))}
          </select>
          <button className="btn-vetme-outline">
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </button>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {performanceMetrics.map((metric, index) => (
          <div key={index} className="card-vetme p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-vetme-gray-dark">{metric.name}</p>
                <p className="text-3xl font-bold text-vetme-dark mt-2">
                  {metric.name.includes('Volume') || metric.name.includes('Transaction') && metric.name.includes('Average')
                    ? formatCurrency(metric.current)
                    : metric.name.includes('Rate')
                    ? `${metric.current}%`
                    : formatNumber(metric.current)
                  }
                </p>
              </div>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                metric.trend === 'up' ? 'bg-green-100' : 'bg-red-100'
              }`}>
                {metric.trend === 'up' ? (
                  <TrendingUp className="w-6 h-6 text-green-600" />
                ) : (
                  <TrendingDown className="w-6 h-6 text-red-600" />
                )}
              </div>
            </div>

            <div className="flex items-center mt-4 pt-4 border-t border-vetme-gray-light">
              {metric.trend === 'up' ? (
                <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
              ) : (
                <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
              )}
              <span className={`text-sm font-medium ${
                metric.trend === 'up' ? 'text-green-600' : 'text-red-600'
              }`}>
                {metric.trend === 'up' ? '+' : ''}{metric.change.toFixed(2)}%
              </span>
              <span className="text-sm text-vetme-gray-dark ml-1">vs previous period</span>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Transaction Volume Chart */}
        <div className="card-vetme p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-vetme-dark">Transaction Volume</h3>
            <BarChart3 className="w-5 h-5 text-vetme-mint-dark" />
          </div>

          <SimpleChart data={volumeData} />

          <div className="mt-4 pt-4 border-t border-vetme-gray-light">
            <div className="flex justify-between text-sm">
              <span className="text-vetme-gray-dark">Average Daily Volume</span>
              <span className="font-medium text-vetme-dark">
                {volumeData.length > 0
                  ? formatCurrency(volumeData.reduce((sum, d) => sum + (d.amount || 0), 0) / volumeData.length)
                  : formatCurrency(0)
                }
              </span>
            </div>
          </div>
        </div>

        {/* Transaction Count Chart */}
        <div className="card-vetme p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-vetme-dark">Daily Transactions</h3>
            <TrendingUp className="w-5 h-5 text-vetme-mint-dark" />
          </div>

          <SimpleChart data={volumeData} />

          <div className="mt-4 pt-4 border-t border-vetme-gray-light">
            <div className="flex justify-between items-center">
              <div className="flex space-x-4">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-400 rounded-full mr-2"></div>
                  <span className="text-sm text-vetme-gray-dark">Onramp</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-blue-400 rounded-full mr-2"></div>
                  <span className="text-sm text-vetme-gray-dark">Offramp</span>
                </div>
              </div>
              <span className="font-medium text-vetme-dark">
                {formatNumber(volumeData.reduce((sum, d) => sum + (d.total || 0), 0))} total
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Top Cryptocurrencies and User Growth */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Cryptocurrencies */}
        <div className="card-vetme p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-vetme-dark">Top Cryptocurrencies</h3>
            <PieChart className="w-5 h-5 text-vetme-mint-dark" />
          </div>

          <div className="space-y-4">
            {topCryptos.length > 0 ? (
              topCryptos.map((crypto, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-medium text-white ${
                      index === 0 ? 'bg-yellow-500' :
                      index === 1 ? 'bg-orange-500' :
                      index === 2 ? 'bg-blue-500' :
                      index === 3 ? 'bg-green-500' :
                      'bg-vetme-gray-dark'
                    }`}>
                      {crypto.name}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-vetme-dark">{crypto.name}</div>
                      <div className="text-sm text-vetme-gray-dark">{crypto.transactions} transactions</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-vetme-dark">
                      {formatCurrency(crypto.volume)}
                    </div>
                    <div className="text-sm text-vetme-gray-dark">{crypto.percentage}%</div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-vetme-gray-dark py-4">
                No cryptocurrency data available
              </div>
            )}
          </div>
        </div>

        {/* Key Insights */}
        <div className="card-vetme p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-vetme-dark">Key Insights</h3>
            <Users className="w-5 h-5 text-vetme-mint-dark" />
          </div>

          <div className="space-y-6">
            {/* Peak Hours */}
            <div>
              <h4 className="text-sm font-medium text-vetme-dark mb-3">Peak Trading Hours</h4>
              <div className="flex items-center justify-between">
                <span className="text-sm text-vetme-gray-dark">Most Active</span>
                <span className="text-sm font-medium text-vetme-dark">2:00 PM - 6:00 PM</span>
              </div>
              <div className="w-full bg-vetme-gray-light rounded-full h-2 mt-2">
                <div className="bg-vetme-mint h-2 rounded-full" style={{ width: '75%' }}></div>
              </div>
            </div>

            {/* User Growth */}
            <div>
              <h4 className="text-sm font-medium text-vetme-dark mb-3">User Growth</h4>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-vetme-gray-dark">New Users This Week</span>
                <span className="text-sm font-medium text-green-600">
                  +{userGrowth.newUsers}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-vetme-gray-dark">Retention Rate</span>
                <span className="text-sm font-medium text-vetme-dark">
                  {userGrowth.retentionRate}%
                </span>
              </div>
            </div>

            {/* Average Processing Time */}
            <div>
              <h4 className="text-sm font-medium text-vetme-dark mb-3">Performance</h4>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-vetme-gray-dark">Avg Processing Time</span>
                <span className="text-sm font-medium text-vetme-dark">6.2 minutes</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-vetme-gray-dark">Success Rate</span>
                <span className="text-sm font-medium text-green-600">
                  {stats.successRate}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Summary */}
      <div className="card-vetme p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-vetme-dark">Monthly Summary</h3>
          <Calendar className="w-5 h-5 text-vetme-mint-dark" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-vetme-dark mb-2">
              {formatCurrency(stats.totalVolume)}
            </div>
            <div className="text-sm text-vetme-gray-dark">Total Volume</div>
            <div className="text-sm font-medium text-green-600 mt-1">
              +16.05% from last month
            </div>
          </div>

          <div className="text-center">
            <div className="text-3xl font-bold text-vetme-dark mb-2">
              {formatNumber(stats.totalTransactions)}
            </div>
            <div className="text-sm text-vetme-gray-dark">Total Transactions</div>
            <div className="text-sm font-medium text-green-600 mt-1">
              +17.45% from last month
            </div>
          </div>

          <div className="text-center">
            <div className="text-3xl font-bold text-vetme-dark mb-2">
              {formatNumber(stats.activeUsers)}
            </div>
            <div className="text-sm text-vetme-gray-dark">Active Users</div>
            <div className="text-sm font-medium text-green-600 mt-1">
              +12.3% from last month
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
